"use client";

import { useRef, useState, type FormEvent } from "react";
import { Spinner } from "@/components/Spinner";
import { submitPublicContact } from "@/lib/actions";

const field =
  "w-full rounded-lg border border-stone-300 px-4 py-3 text-sm focus:border-stone-500 focus:outline-none";
const btn =
  "w-full rounded-lg bg-stone-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50";

export function ContactFlow({ initialEmail = "", stamp }: { initialEmail?: string; stamp: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  // The honeypot, deliberately UNCONTROLLED and read from the DOM at submit.
  // A React-controlled field would only see what React put there — and a bot
  // sets input.value directly, which React state never hears about. Read the
  // element, not our idea of it.
  const website = useRef<HTMLInputElement>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await submitPublicContact({
      email,
      subject,
      body,
      website: website.current?.value ?? "",
      stamp,
    });
    setBusy(false);
    if (!r.ok) return setError(r.error ?? "Couldn't send. Try again.");
    setSent(true);
  }

  if (sent) {
    return (
      <div className="rounded-lg bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
        Message sent. We reply to <span className="font-medium">{email}</span> — usually the same
        day. You do not need to keep this page open.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {error && (
        <p className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>
      )}
      <div>
        <label className="text-xs uppercase tracking-wide text-stone-500">Your email</label>
        <input suppressHydrationWarning
          type="email"
          required
          className={`${field} mt-1`}
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wide text-stone-500">Subject</label>
        <input suppressHydrationWarning
          required
          className={`${field} mt-1`}
          placeholder="What is this about?"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wide text-stone-500">Message</label>
        <textarea suppressHydrationWarning
          required
          rows={6}
          className={`${field} mt-1`}
          placeholder="Tell us what happened, in your own words."
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      {/* Honeypot: off-screen, not focusable, ignored by screen readers. Bots
          fill every input they find; people never see this one. */}
      <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="website">Website</label>
        <input suppressHydrationWarning
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          ref={website}
          defaultValue=""
        />
      </div>
      <button type="submit" disabled={busy} className={btn}>
        {busy ? <Spinner tone="ink" label="Sending…" /> : "Send message"}
      </button>
    </form>
  );
}
